/**
 * Saved Imports page — reusable CSV/Excel import definitions.
 *
 * Admin sees: every definition + Edit (full config, via the wizard) / Grants / Delete.
 * Granted user sees: the definitions they may run as cards with a Run button.
 *
 * Definitions are CREATED from the import wizard's "Save as reusable import"
 * (launched against a real table, where columns are known) — there is no
 * standalone "New" here. Running one re-opens the wizard pre-filled at the
 * upload step; the saved mapping re-applies to the freshly chosen file.
 */
import { createResource, createSignal, Show, For } from 'solid-js';
import { api } from '../lib/api.js';
import { currentUser } from '../lib/auth-store.js';
import { roleLabel } from '../lib/roles.js';
import { toast } from '../lib/toast.js';
import Modal from '../components/Modal.jsx';
import ImportRunner from '../components/ImportRunner.jsx';
import ImportDialog from '../components/ImportDialog.jsx';
import ImportDslPanel from './ImportDslPanel.jsx';
import {
  listImportDefs, deleteImportDef,
  getImportGrants, setImportGrants,
} from '../lib/importdefs.js';

export default function SavedImports() {
  const isAdmin = () => currentUser()?.role === 'admin';

  const [defs, { refetch }] = createResource(async () => {
    try { return await listImportDefs(); } catch (e) { toast.error(e.message); return []; }
  });

  const [running, setRunning] = createSignal(null);     // { def, config }
  const [editingDef, setEditingDef] = createSignal(null); // { def, config }
  const [grantTarget, setGrantTarget] = createSignal(null);
  const [picking, setPicking] = createSignal(false);    // New-import target picker open
  const [creating, setCreating] = createSignal(null);   // { connectionId, database, table }
  const [dslEditing, setDslEditing] = createSignal(null); // null | { mode:'new' } | { mode:'edit', id }

  // parseConfig parses a definition's config, normalising a legacy Commit-2
  // {mapping:{target:source}} map into the current {columns:[…]} shape.
  const parseConfig = (def) => {
    let config;
    try { config = JSON.parse(def.config || '{}'); }
    catch { toast.error('This definition has a corrupt config.'); return null; }
    if (!config.columns && config.mapping) {
      config.columns = Object.entries(config.mapping)
        .map(([target, source]) => ({ target, mode: 'input', source }));
    }
    return config;
  };

  const runDef = (def) => { const c = parseConfig(def); if (c) setRunning({ def, config: c }); };
  const editDef = (def) => { const c = parseConfig(def); if (c) setEditingDef({ def, config: c }); };

  const removeDef = async (def) => {
    if (!confirm(`Delete saved import "${def.name}"? This cannot be undone.`)) return;
    try {
      await deleteImportDef(def.id);
      toast.success(`Deleted "${def.name}"`);
      refetch();
    } catch (e) { toast.error(e.message); }
  };

  // Authoring as DSL takes over the page (the editor needs the room).
  return (
    <Show when={!dslEditing()} fallback={
      <div class="forms-dsl-host">
        <ImportDslPanel
          editId={dslEditing().mode === 'edit' ? dslEditing().id : null}
          onBack={() => { setDslEditing(null); refetch(); }}
          onSaved={() => refetch()}
        />
      </div>
    }>
    <div class="page">
      <header class="page-header">
        <div>
          <h1>Saved Imports</h1>
          <p class="muted">
            {isAdmin()
              ? 'Reusable CSV/Excel import configs. Author one as DSL, build one in the wizard, or from a table’s right-click → Import data… → “Save as reusable import”.'
              : 'Import configs you can run — pick a file and the saved column mapping re-applies.'}
          </p>
        </div>
        <Show when={isAdmin()}>
          <div style="display:flex;gap:8px;">
            <button class="btn" onClick={() => setDslEditing({ mode: 'new' })}>+ New (DSL)</button>
            <button class="btn btn-ghost" onClick={() => setPicking(true)}>+ New (wizard)</button>
          </div>
        </Show>
      </header>

      <Show when={defs()} fallback={<div class="muted">Loading…</div>}>
        <Show when={defs().length > 0} fallback={
          <div class="empty-state">
            <h2>No saved imports</h2>
            <p class="muted">
              {isAdmin()
                ? 'Author one as DSL, use the wizard, or a table’s right-click → Import data… → “Save as reusable import”.'
                : 'Ask an admin to grant you a reusable import.'}
            </p>
            <Show when={isAdmin()}>
              <div style="display:flex;gap:8px;justify-content:center;">
                <button class="btn" onClick={() => setDslEditing({ mode: 'new' })}>+ New (DSL)</button>
                <button class="btn btn-ghost" onClick={() => setPicking(true)}>+ New (wizard)</button>
              </div>
            </Show>
          </div>
        }>
          <div class="forms-grid">
            <For each={defs()}>
              {(def) => (
                <div class="form-card">
                  <header>
                    <div>
                      <h3>{def.name} <Show when={def.script}><span class="import-badge">DSL</span></Show></h3>
                      <div class="form-card-meta mono muted">
                        {def.connectionName ? `${def.connectionName} · ` : ''}{def.databaseName} / {def.tableName}
                      </div>
                      <Show when={def.description}><p class="muted" style="font-size:12.5px;">{def.description}</p></Show>
                    </div>
                  </header>
                  <footer>
                    <button class="btn" onClick={() => runDef(def)}>Run</button>
                    <Show when={isAdmin()}>
                      <button class="btn btn-ghost"
                              onClick={() => def.script ? setDslEditing({ mode: 'edit', id: def.id }) : editDef(def)}>
                        {def.script ? 'Edit DSL' : 'Edit'}
                      </button>
                      <button class="btn btn-ghost" onClick={() => setGrantTarget(def)}>Grants</button>
                      <button class="btn btn-ghost btn-danger" onClick={() => removeDef(def)}>Delete</button>
                    </Show>
                  </footer>
                </div>
              )}
            </For>
          </div>
        </Show>
      </Show>

      {/* Run a saved import — the stripped-down runner (file + validate + import).
          Generate-mode falls back to a copyable SQL modal inside ImportRunner. */}
      <Show when={running()}>
        <ImportRunner
          defId={running().def.id}
          defName={running().def.name}
          table={running().def.tableName}
          definition={running().config}
          onClose={() => setRunning(null)}
        />
      </Show>

      {/* Edit (admin) — the full wizard pre-filled; upload a sample file to adjust
          the mapping, then "Save changes" updates this definition. */}
      <Show when={editingDef()}>
        <ImportDialog
          connectionId={editingDef().def.connectionId}
          database={editingDef().def.databaseName}
          table={editingDef().def.tableName}
          definition={editingDef().config}
          editId={editingDef().def.id}
          editName={editingDef().def.name}
          editDesc={editingDef().def.description}
          onSaved={() => { setEditingDef(null); refetch(); }}
          onClose={() => setEditingDef(null)}
        />
      </Show>

      {/* Grants (admin) */}
      <Show when={grantTarget()}>
        <ImportGrantModal def={grantTarget()} onClose={() => setGrantTarget(null)} />
      </Show>

      {/* New import (admin): pick the target table, then build + save in the wizard.
          Mirrors the Forms "+ New Form" flow — the dialog needs a real table's
          columns, so we gather connection/database/table first. */}
      <Show when={picking()}>
        <NewImportPicker
          onPick={(connectionId, database, table) => { setPicking(false); setCreating({ connectionId, database, table }); }}
          onClose={() => setPicking(false)}
        />
      </Show>
      <Show when={creating()}>
        <ImportDialog
          connectionId={creating().connectionId}
          database={creating().database}
          table={creating().table}
          onSaved={() => { setCreating(null); refetch(); }}
          onClose={() => setCreating(null)}
        />
      </Show>
    </div>
    </Show>
  );
}

// ── New-import target picker (connection → database → table) ─────────────────────
// Gathers what ImportDialog needs to open in create mode, mirroring the Form
// wizard's Step 1. Reuses the same /databases and /objects endpoints.
function NewImportPicker(props) {
  const [connId, setConnId] = createSignal('');
  const [dbName, setDbName] = createSignal('');
  const [table, setTable] = createSignal('');

  const [conns] = createResource(async () => {
    try { return (await api.get('/api/connections')).connections || []; } catch { return []; }
  });
  const [databases] = createResource(() => connId() || null, async (id) => {
    try { return (await api.get(`/api/connections/${id}/databases`)).databases || []; } catch { return []; }
  });
  const [tables] = createResource(
    () => (connId() && dbName()) ? [connId(), dbName()] : null,
    async ([id, db]) => {
      try { return (await api.get(`/api/connections/${id}/databases/${encodeURIComponent(db)}/objects`)).tables || []; }
      catch { return []; }
    },
  );

  return (
    <Modal open={true} onClose={props.onClose} title="New reusable import">
      <p class="muted" style="margin-bottom:12px;">
        Pick the target table. Next you’ll upload a sample file, map the columns, and “Save as reusable import”.
      </p>

      <label class="field-label">Connection</label>
      <select class="input" onChange={(e) => { setConnId(e.target.value); setDbName(''); setTable(''); }}>
        <option value="" selected={!connId()}>— select —</option>
        <For each={conns()}>
          {(c) => <option value={c.id} selected={String(c.id) === String(connId())}>{c.name}</option>}
        </For>
      </select>

      <Show when={connId()}>
        <label class="field-label" style="margin-top:12px;">Database</label>
        <select class="input" onChange={(e) => { setDbName(e.target.value); setTable(''); }}>
          <option value="" selected={!dbName()}>— select —</option>
          <For each={databases()}>
            {(d) => <option value={d.name} selected={d.name === dbName()}>{d.name}</option>}
          </For>
        </select>
      </Show>

      <Show when={dbName()}>
        <label class="field-label" style="margin-top:12px;">Table</label>
        <select class="input" onChange={(e) => setTable(e.target.value)}>
          <option value="" selected={!table()}>— select —</option>
          <For each={tables()}>
            {(t) => <option value={t.name} selected={t.name === table()}>{t.name}</option>}
          </For>
        </select>
      </Show>

      <div style="margin-top:16px;display:flex;gap:8px;">
        <button class="btn" disabled={!table()} onClick={() => props.onPick(Number(connId()), dbName(), table())}>Continue →</button>
        <button class="btn btn-ghost" onClick={props.onClose}>Cancel</button>
      </div>
    </Modal>
  );
}

// ── Grants (per-definition; mirrors FormGrantModal) ─────────────────────────────
function ImportGrantModal(props) {
  // Only role 'user' can run imports (admins always can; readonly can't write).
  const [users] = createResource(async () => {
    const r = await api.get('/api/users');
    return (r.users || []).filter((u) => u.role === 'user');
  });

  const [grants, setGrants] = createSignal(new Set());
  const [loaded, setLoaded] = createSignal(false);
  const [saving, setSaving] = createSignal(false);

  createResource(
    () => (users() ? props.def.id : null),
    async () => {
      const granted = new Set();
      await Promise.all((users() || []).map(async (u) => {
        try { if ((await getImportGrants(u.id)).includes(props.def.id)) granted.add(u.id); }
        catch { /* ignore */ }
      }));
      setGrants(granted);
      setLoaded(true);
    },
  );

  const toggle = (uid) => setGrants((prev) => {
    const next = new Set(prev);
    if (next.has(uid)) next.delete(uid); else next.add(uid);
    return next;
  });

  const save = async () => {
    setSaving(true);
    try {
      await Promise.all((users() || []).map(async (u) => {
        const current = new Set(await getImportGrants(u.id));
        if (grants().has(u.id)) current.add(props.def.id); else current.delete(props.def.id);
        await setImportGrants(u.id, [...current]);
      }));
      toast.success('Grants saved');
      props.onClose();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal open={true} onClose={props.onClose} title={`Grants: ${props.def.name}`}>
      <Show when={loaded()} fallback={<div class="muted">Loading users…</div>}>
        <p class="muted" style="margin-bottom:12px;">
          Select which users can run this import. Admins always can; readonly users cannot run imports.
        </p>
        <Show when={(users() || []).length > 0} fallback={<p class="muted">No standard (role “user”) accounts to grant.</p>}>
          <div class="grant-user-list">
            <For each={users()}>
              {(u) => (
                <label class="checkbox-label">
                  <input type="checkbox" checked={grants().has(u.id)} onChange={() => toggle(u.id)} />
                  <span>{u.username}</span>
                  <span class={`badge-role ${u.role}`}>{roleLabel(u.role)}</span>
                </label>
              )}
            </For>
          </div>
        </Show>
        <div style="margin-top:16px;display:flex;gap:8px;">
          <button class="btn" disabled={saving()} onClick={save}>{saving() ? 'Saving…' : 'Save Grants'}</button>
          <button class="btn btn-ghost" onClick={props.onClose}>Cancel</button>
        </div>
      </Show>
    </Modal>
  );
}
